import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, ButtonBase, Chip, ClickAwayListener, Divider, ListItemButton, ListItemText, Paper, Popper, Stack, Typography } from '@mui/material';
import { ChevronDown, LogOut } from 'lucide-react';

import { selectCurrentUser, selectCompanyName, selectUserRoles, logoutUser } from '../../../../../Redux/slices/authSlice';

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || 'U';

const Avatar = ({ name, size = 30 }) => (
  <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: 'primary.lighter', color: 'primary.dark', display: 'grid', placeItems: 'center', fontSize: size * 0.38, fontWeight: 600, flexShrink: 0 }}>
    {initials(name)}
  </Box>
);

// ==============================|| HEADER - PROFILE ||============================== //

export default function Profile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const companyName = useSelector(selectCompanyName);
  const roles = useSelector(selectUserRoles);

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const logout = async () => {
    await dispatch(logoutUser());
    navigate('/login');
  };

  return (
    <Box sx={{ flexShrink: 0 }}>
      <ButtonBase ref={anchorRef} onClick={() => setOpen((v) => !v)} sx={{ gap: 1, pl: 0.5, pr: 1, py: 0.5, borderRadius: '8px', '&:hover': { bgcolor: 'grey.100' } }}>
        <Avatar name={user?.name} />
        <Box sx={{ textAlign: 'left', display: { xs: 'none', md: 'block' }, lineHeight: 1.2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'grey.800' }} noWrap>{user?.name}</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'grey.500' }} noWrap>{companyName}</Typography>
        </Box>
        <ChevronDown size={14} color="#8c8c8c" />
      </ButtonBase>

      <Popper placement="bottom-end" open={open} anchorEl={anchorRef.current} disablePortal modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]} sx={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={(e) => { if (!anchorRef.current?.contains(e.target)) setOpen(false); }}>
          <Paper variant="outlined" sx={{ width: 280, boxShadow: '0 10px 28px rgba(16,24,40,0.12)', overflow: 'hidden' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2 }}>
              <Avatar name={user?.name} size={40} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>{user?.name}</Typography>
                <Typography sx={{ fontSize: 12, color: 'grey.500' }} noWrap>{user?.userName ? `@${user.userName}` : user?.email}</Typography>
              </Box>
            </Stack>
            <Box sx={{ px: 2, pb: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: 'grey.500', mb: 0.75 }} noWrap>{companyName}</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {(roles || []).map((r) => <Chip key={r} label={r} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />)}
              </Stack>
            </Box>
            <Divider />
            <ListItemButton onClick={logout} sx={{ py: 1.25, color: 'error.main', gap: 1.25 }}>
              <LogOut size={16} strokeWidth={1.7} />
              <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }} />
            </ListItemButton>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
